import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';

import {
  getSourceProps,
  getSourceTransforms,
} from '../../canvas-elements/element-model/transformations';
import { parkedToggleButtonProps } from '../toggle-button/toggle-button.transforms';

type DropdownElement = CanvasElementFrom<'dropdown'>;
type DropdownProps = PartialDeep<PropsOf<'dropdown'>>;

function transformCheckboxToDropdown(source: CanvasElementFrom<'checkbox'>): DropdownElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'checkbox', 'dropdown'>(source, 'dropdown');
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      menu: {
        placeholder: propsT?.content?.menu?.placeholder,
        values: props.content?.checkbox?.values,
        preselect: propsT?.content?.menu?.preselect,
        prefix: propsT?.content?.menu?.prefix,
        suffix: propsT?.content?.menu?.suffix,
        dynamicSearch: propsT?.content?.menu?.dynamicSearch,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
        offset: props.style?.general?.offset,
      },
      input: {
        color: props.style?.checkbox?.label,
        fill: props.style?.checkbox?.fill,
        border: props.style?.checkbox?.border,
        radius: props.style?.checkbox?.radius,
      },
      list: propsT?.style?.list,
    },
    transforms: {
      checkbox: {
        content: {
          checkbox: {
            layout: props?.content?.checkbox?.layout,
            readonly: props?.content?.checkbox?.readonly,
            minWidth: props?.content?.checkbox?.minWidth,
            maxColumns: props?.content?.checkbox?.maxColumns,
          },
        },
        style: {
          checkbox: {
            symbol: props.style?.checkbox?.symbol,
          },
        },
      } as PartialDeep<PropsOf<'checkbox'>>,
    },
  };

  return { props: newProps } as any;
}

function transformImageToDropdown(source: CanvasElementFrom<'image'>): DropdownElement {
  const props = getSourceProps(source);
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: undefined, // no mapping
      },
      menu: {
        placeholder: undefined, // no mapping
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        prefix: undefined, // no mapping
        suffix: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: undefined, // no mapping
      },
      input: {
        color: undefined, // no mapping
        fill: undefined, // no mapping
        border: props.style?.outline?.color,
        radius: props.style?.outline?.radius,
      },
      list: {
        value: undefined, // no mapping
        background: undefined, // no mapping
        border: props.style?.outline?.color,
        radius: props.style?.outline?.radius,
      },
    },
  };

  return { props: newProps } as any;
}

function transformRadioToDropdown(source: CanvasElementFrom<'radio'>): DropdownElement {
  const props = getSourceProps(source);
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      menu: {
        placeholder: undefined, // no mapping
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        prefix: undefined, // no mapping
        suffix: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.radio?.label,
        fill: props.style?.radio?.fill,
        border: props.style?.radio?.border,
        radius: undefined, // no mapping
      },
      list: {
        value: props.style?.radio?.label,
        background: props.style?.radio?.fill,
        border: props.style?.radio?.border,
        radius: undefined, // no mapping
      },
    },
  };

  return { props: newProps } as any;
}

function transformTagsToDropdown(source: CanvasElementFrom<'tags'>): DropdownElement {
  const props = getSourceProps(source);
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      menu: {
        placeholder: props.content?.tags?.placeholder,
        values: props.content?.tags?.values,
        preselect: props.content?.tags?.preselect?.[0],
        prefix: undefined, // no mapping
        suffix: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      list: {
        value: props.style?.list?.value,
        background: props.style?.list?.background,
        border: props.style?.list?.border,
        radius: props.style?.list?.radius,
      },
    },
  };

  return { props: newProps } as any;
}

function transformTextFieldToDropdown(source: CanvasElementFrom<'textField'>): DropdownElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'textField', 'dropdown'>(source, 'dropdown');
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: propsT?.content?.data?.defaultValue,
      },
      menu: {
        placeholder: props.content?.input?.placeholder,
        values: propsT?.content?.menu?.values,
        preselect: propsT?.content?.menu?.preselect?.[0],
        prefix: propsT?.content?.menu?.prefix,
        suffix: propsT?.content?.menu?.suffix,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      list: {
        value: propsT?.style?.list?.value,
        background: propsT?.style?.list?.background,
        border: propsT?.style?.list?.border,
        radius: propsT?.style?.list?.radius,
      },
    },
    transforms: {
      textField: {
        content: {
          input: {
            format: props.content?.input?.format,
            precision: props.content?.input?.precision,
            currency: props.content?.input?.currency,
          },
          container: {
            overflow: props.content?.container?.overflow,
          },
        },
      } as PartialDeep<PropsOf<'textField'>>,
    },
  };

  return { props: newProps } as DropdownElement;
}

function transformTextInputToDropdown(source: CanvasElementFrom<'textInput'>): DropdownElement {
  const props = getSourceProps(source);
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align?.horizontal,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.input?.placeholder ? 'placeholder' : undefined,
      },
      menu: {
        placeholder: props.content?.input?.placeholder,
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        prefix: undefined, // no mapping
        suffix: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        color: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
      list: {
        value: props.style?.input?.value,
        background: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  return { props: newProps } as any;
}

function transformUploadFileToDropdown(source: CanvasElementFrom<'uploadFile'>): DropdownElement {
  const props = getSourceProps(source);
  const newProps: DropdownProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: undefined, // no mapping
      },
      menu: {
        placeholder: undefined, // no mapping
        values: undefined, // no mapping
        preselect: undefined, // no mapping
        prefix: undefined, // no mapping
        suffix: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: undefined, // no mapping
      },
      input: {
        color: props.style?.uploaded?.label,
        fill: props.style?.uploaded?.fill,
        border: props.style?.uploaded?.outline,
        radius: props.style?.button?.radius,
      },
      list: {
        value: props.style?.uploaded?.label,
        background: props.style?.uploaded?.fill,
        border: props.style?.uploaded?.outline,
        radius: props.style?.button?.radius,
      },
    },
  };

  return { props: newProps } as any;
}

function transformToggleButtonToDropdown(
  source: CanvasElementFrom<'toggleButton'>,
): DropdownElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'toggleButton', 'dropdown'>(source, 'dropdown');
  const newProps: DropdownProps = {
    content: {
      title: { ...props.content?.title },
      data: {
        defaultValue: propsT?.content?.data?.defaultValue,
      },
      menu: {
        placeholder: propsT?.content?.menu?.placeholder,
        values: propsT?.content?.menu?.values,
        preselect: propsT?.content?.menu?.preselect,
        prefix: propsT?.content?.menu?.prefix,
        suffix: propsT?.content?.menu?.suffix,
        dynamicSearch: propsT?.content?.menu?.dynamicSearch,
      },
    },
    style: {
      general: {
        title: props.style?.general?.title,
        accent: propsT?.style?.general?.accent,
        offset: props.style?.button?.offset,
      },
      input: propsT?.style?.input,
      list: propsT?.style?.list,
    },
    transforms: {
      toggleButton: parkedToggleButtonProps(props),
    },
  };

  return { props: newProps } as unknown as DropdownElement;
}

export const transformToDropdown = {
  transformCheckboxToDropdown,
  transformImageToDropdown,
  transformRadioToDropdown,
  transformTagsToDropdown,
  transformTextFieldToDropdown,
  transformTextInputToDropdown,
  transformToggleButtonToDropdown,
  transformUploadFileToDropdown,
};
